import React from 'react';
import clsx from 'clsx';

/**
 * Cards, panels and popovers.
 *
 * `tone` supplies the colours (background, border, shadow); shape and spacing
 * come from `className`, so every card keeps its own look while its colours come
 * from this one list. All colours are theme tokens. For a complete card with a
 * title and body, use `Card`.
 */

export const SURFACE_TONES = {
  /** White card with a light border. */
  card: 'bg-white border-gray-200',
  /** White card with a soft shadow. */
  'card-soft': 'bg-white border-gray-200 shadow-sm',
  /** White card that lifts on hover. */
  'card-hover': 'bg-white border-gray-200 shadow-sm hover:shadow-md',
  /** White card with a stronger shadow. */
  'card-raised': 'bg-white border-gray-200 shadow-lg',
  /** White card with the app's own border colour. */
  'card-agentic': 'bg-white border-agentic-gray-200',
  /** Floating panel (menus, dialogs). */
  popover: 'bg-white border-gray-100 shadow-xl',
  /** White background only. */
  plain: 'bg-white',
  /** No colours of its own: all colours come from `className`. */
  custom: '',
} as const;

export type SurfaceTone = keyof typeof SURFACE_TONES;

type SurfaceTag = 'div' | 'section' | 'article' | 'aside' | 'header' | 'footer' | 'form' | 'li';

export type SurfaceProps = React.HTMLAttributes<HTMLElement> & {
  tone?: SurfaceTone;
  as?: SurfaceTag;
};

export const Surface = React.forwardRef<HTMLElement, SurfaceProps>(
  ({ tone = 'custom', as = 'div', className, ...props }, ref) =>
    React.createElement(as, { ref, className: clsx(SURFACE_TONES[tone], className) || undefined, ...props })
);

Surface.displayName = 'Surface';
